import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import { Action, type SupportedLanguage } from "@packages/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Building2,
	Check as CheckIcon,
	Copy,
	Globe,
	Loader2,
	MapPin,
	Wallet,
} from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	COUNTRY_CODES,
	type CountryCode,
	countryLabel,
	DEFAULT_COUNTRY_CODE,
	useUpdateOrganization,
} from "@/api/organizations";
import { LanguageFlag } from "@/components/LanguageFlag";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasPermission } from "@/hooks/useAccessControl";
import { type LanguageKey, languages } from "@/i18n";
import { DailyDigestToggle } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/DailyDigestToggle";
import { OrganizationProfileForm } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationProfileForm";
import { SettingsLayout } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/SettingsLayout";
import { cn } from "@/utils/utils";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/settings/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const languageSelectId = useId();
	const countrySelectId = useId();

	// Fetch user organizations to get the current organization details
	const { data: organizations } = useSuspenseQuery(
		getMyOrganizationsQueryOptions(),
	);

	// Find the current organization
	const currentOrg = organizations.find((org) => org.id === orgId);

	// UX gate only — the route's `MANAGE_SETTINGS` macro is the real control.
	const canEdit = useHasPermission()(Action.MANAGE_SETTINGS);

	// State for language selection
	const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(
		(currentOrg?.defaultLanguage as SupportedLanguage) || "en_US",
	);
	const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
		currentOrg?.country ?? DEFAULT_COUNTRY_CODE,
	);

	// Update local state when organization data changes
	useEffect(() => {
		if (currentOrg?.defaultLanguage) {
			setSelectedLanguage(currentOrg.defaultLanguage as SupportedLanguage);
		}
	}, [currentOrg?.defaultLanguage]);

	useEffect(() => {
		if (currentOrg?.country) {
			setSelectedCountry(currentOrg.country);
		}
	}, [currentOrg?.country]);

	const updateOrganizationMutation = useUpdateOrganization();

	// Read-only: currency is fixed at organization creation and cannot be changed.
	const currencyLabels: Record<string, string> = {
		BRL: t("organization.currency.brl"),
		USD: t("organization.currency.usd"),
	};
	const currencyValue = currentOrg?.defaultCurrency ?? "BRL";

	const handleLanguageChange = (value: string) => {
		const newLanguage = value as SupportedLanguage;
		setSelectedLanguage(newLanguage);

		// Save immediately on change
		updateOrganizationMutation.mutate({
			organizationId: orgId,
			data: {
				defaultLanguage: newLanguage,
			},
		});
	};

	const handleCountryChange = (value: string) => {
		const newCountry = value as CountryCode;
		setSelectedCountry(newCountry);
		updateOrganizationMutation.mutate({
			organizationId: orgId,
			data: { country: newCountry },
		});
	};

	return (
		<SettingsLayout
			activeTab="general"
			title={t("common.general")}
			testId="organization-settings-container"
			contentTestId="organization-settings-main-content-area"
		>
			<div className="space-y-3 md:space-y-4">
				{/* Organization Details Card */}
				<Card>
					<CardContent className="py-3 md:py-4 px-3 md:px-6">
						<div className="space-y-4 md:space-y-6">
							{/* Organization Details */}
							<div className="space-y-3 md:space-y-4">
								<div className="flex items-center space-x-2 md:space-x-3">
									<div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-base-300 flex items-center justify-center shrink-0">
										<Building2 className="w-5 h-5 md:w-8 md:h-8 text-base-content/70" />
									</div>
									<div className="flex-1 min-w-0">
										<OrganizationProfileForm
											organizationId={orgId}
											name={currentOrg?.name ?? ""}
											description={currentOrg?.description}
											canEdit={canEdit}
										/>
										<div className="mt-2">
											<OrganizationIdCopy orgId={orgId} />
										</div>
									</div>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
					<Card className="min-w-0 h-full">
						<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
							<CardTitle className="flex items-center gap-2 text-sm md:text-base">
								<MapPin className="w-4 h-4 md:w-5 md:h-5" />
								{t("organization.country.label")}
							</CardTitle>
						</CardHeader>
						<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
							<div className="space-y-2">
								<p
									className="text-sm md:text-base font-medium text-base-content"
									data-testid="organization-country-value"
								>
									{countryLabel(t, selectedCountry)}
								</p>
								{canEdit ? (
									<div className="flex items-center gap-2">
										<Label htmlFor={countrySelectId} className="sr-only">
											{t("organization.country.label")}
										</Label>
										<Select
											value={selectedCountry}
											onValueChange={handleCountryChange}
											disabled={updateOrganizationMutation.isPending}
										>
											<SelectTrigger
												className="w-full"
												id={countrySelectId}
												data-testid="organization-country-trigger"
											>
												<SelectValue
													placeholder={t("organization.country.placeholder")}
												/>
											</SelectTrigger>
											<SelectContent>
												{COUNTRY_CODES.map((code) => (
													<SelectItem key={code} value={code}>
														{countryLabel(t, code)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{updateOrganizationMutation.isPending && (
											<Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
										)}
									</div>
								) : null}
								<p className="text-xs text-base-content/60">
									{t("organization.country.help")}
								</p>
							</div>
						</CardContent>
					</Card>

					<Card className="min-w-0 h-full">
						<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
							<CardTitle className="flex items-center gap-2 text-sm md:text-base">
								<Globe className="w-4 h-4 md:w-5 md:h-5" />
								{t("common.language")}
							</CardTitle>
						</CardHeader>
						<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
							<div className="space-y-3 md:space-y-4">
								<div className="space-y-2">
									<Label
										htmlFor={languageSelectId}
										className="text-xs md:text-sm"
									>
										{t("common.defaultLanguage")}
									</Label>
									<div className="flex items-center gap-2">
										<Select
											value={selectedLanguage}
											onValueChange={handleLanguageChange}
											disabled={updateOrganizationMutation.isPending}
										>
											<SelectTrigger className="w-full" id={languageSelectId}>
												<SelectValue
													placeholder={t("orgSettings.language.selectLanguage")}
												/>
											</SelectTrigger>
											<SelectContent>
												{Object.entries(languages).map(([key, lang]) => (
													<SelectItem key={key} value={key}>
														<span className="flex items-center gap-2">
															<LanguageFlag language={key as LanguageKey} />
															<span>{lang.nativeName}</span>
														</span>
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{updateOrganizationMutation.isPending && (
											<Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
										)}
									</div>
									<p className="text-xs text-base-content/60">
										{t("common.emailLanguageNote")}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Daily attention digest opt-out (per user, per organization) */}
				<DailyDigestToggle organizationId={orgId} />

				{/* Financial Currency (read-only — immutable after creation) */}
				<Card>
					<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
						<CardTitle className="flex items-center gap-2 text-sm md:text-base">
							<Wallet className="w-4 h-4 md:w-5 md:h-5" />
							{t("orgSettings.currency.title")}
						</CardTitle>
					</CardHeader>
					<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
						<div className="space-y-2">
							<p
								className="text-sm md:text-base font-medium text-base-content"
								data-testid="organization-currency-value"
							>
								{currencyLabels[currencyValue] ?? currencyValue}
							</p>
							<p className="text-xs text-base-content/60">
								{t("orgSettings.currency.immutableNote")}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		</SettingsLayout>
	);
}

/**
 * Read-only organization ID with a one-tap copy control. Mirrors the CopyableId
 * pattern used in the media viewer (icon swaps to a check + toast on success).
 */
function OrganizationIdCopy({ orgId }: { orgId: string }) {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(orgId);
			setCopied(true);
			toast.success(t("common.idCopied"));
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			toast.error(t("common.idCopyFailed"));
		}
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					onClick={onCopy}
					className={cn(
						"group inline-flex max-w-full items-center gap-1.5 rounded-md border border-base-300 bg-base-200/60 px-2 py-0.5",
						"font-mono text-[11px] tracking-tight text-base-content/70",
						"transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary",
					)}
					aria-label={t("common.copyId")}
				>
					<span className="text-base-content/40 group-hover:text-primary/70">
						{t("common.organizationId")}
					</span>
					<span className="truncate">{orgId}</span>
					{copied ? (
						<CheckIcon className="h-3 w-3 shrink-0 text-success" />
					) : (
						<Copy className="h-3 w-3 shrink-0 opacity-60" />
					)}
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">{t("common.copyId")}</TooltipContent>
		</Tooltip>
	);
}
